import { pb } from "./pocketbase";
import { bomService } from "./bomService";
import { loteService } from "./loteService";
import { resolveCatalogSku } from "../utils/skuUtils";

export const findItemInCatalog = (sku, catalogMap) => {
  return resolveCatalogSku(sku, catalogMap);
};

// Helper to check if SKU or category belongs to a ring
export const isRing = (sku, category, catalogMap) => {
  const cleanSku = (sku || "").trim().toUpperCase();
  const cleanCat = (category || "").trim().toLowerCase();
  
  if (
    cleanSku.startsWith("AN-") ||
    cleanSku.startsWith("ANL-") ||
    cleanSku.startsWith("ANL") ||
    cleanCat === "anel" ||
    cleanCat === "anéis" ||
    cleanCat === "aneis" ||
    cleanCat === "aliança" ||
    cleanCat === "alianças"
  ) {
    return true;
  }

  // Check name or catalog if catalogMap is provided
  if (catalogMap) {
    // 1. Try direct lookup using hierarchy
    const directItem = findItemInCatalog(cleanSku, catalogMap);
    if (directItem) {
      const nameLower = (directItem.name || "").toLowerCase();
      const catLower = (directItem.category || "").toLowerCase();
      if (
        nameLower.startsWith("anel") ||
        nameLower.startsWith("aliança") ||
        nameLower.includes("anel ") ||
        catLower === "anel" ||
        catLower === "anéis" ||
        catLower === "aneis" ||
        catLower === "aliança" ||
        catLower === "alianças"
      ) {
        return true;
      }
    }

    // 2. Try parsing size suffix and looking up parent
    const parsed = parseRingSku(cleanSku);
    if (parsed.size) {
      const parentItem = findItemInCatalog(parsed.baseSku, catalogMap);
      if (parentItem) {
        const nameLower = (parentItem.name || "").toLowerCase();
        const catLower = (parentItem.category || "").toLowerCase();
        if (
          nameLower.startsWith("anel") ||
          nameLower.startsWith("aliança") ||
          nameLower.includes("anel ") ||
          catLower === "anel" ||
          catLower === "anéis" ||
          catLower === "aneis" ||
          catLower === "aliança" ||
          catLower === "alianças"
        ) {
          return true;
        }
      }
    }
  }

  return false;
};

// Helper to extract ring size and base SKU
export const parseRingSku = (sku) => {
  if (!sku) return { baseSku: "", size: "" };
  const cleanSku = sku.trim().toUpperCase();
  const parts = cleanSku.split("-");
  if (parts.length < 2) return { baseSku: cleanSku, size: "" };
  
  const lastPart = parts[parts.length - 1];
  // Size is usually a number (e.g. "16")
  if (/^\d+$/.test(lastPart)) {
    const size = lastPart;
    const baseSku = parts.slice(0, parts.length - 1).join("-");
    return { baseSku, size };
  }
  
  return { baseSku: cleanSku, size: "" };
};

// Helper to generate ring SKU with correct Z/size format
export const getRingSkuWithSize = (baseSku, size) => {
  const cleanBase = String(baseSku).trim().toUpperCase();
  if (cleanBase.endsWith("-Z")) {
    return `${cleanBase}-${size}`;
  }
  return `${cleanBase}-Z-${size}`;
};

// Standard ring sizes list
export const ALL_RING_SIZES = Array.from({ length: 21 }, (_, i) => String(i + 10)); // 10 to 30

export const PLANNING_CATEGORIES = ["FORMATURA", "BERLOQUES", "SACRO", "ESPIRITUALISTA", "ALIANÇAS", "OUTROS"];



export const resolvePlanningCategory = (item) => {
  if (!item) return "OUTROS";
  const sku = (item.sku || "").trim().toUpperCase();
  const name = (item.name || "").trim().toUpperCase();
  const category = (item.category || "").trim().toUpperCase();

  // 1. ALIANÇAS:
  if (
    sku.includes("ALI") ||
    name.includes("ALIANÇA") ||
    name.includes("ALIANCA") ||
    category.includes("ALIANÇA") ||
    category.includes("ALIANCA")
  ) {
    return "ALIANÇAS";
  }

  // 2. SACRO:
  if (
    sku.startsWith("MEDAL") ||
    sku.startsWith("SCR") ||
    sku.includes("MDL") ||
    name.includes("SACRO") ||
    name.includes("CRUZ") ||
    name.includes("TERÇO") ||
    name.includes("TERCO") ||
    name.includes("NOSSA SENHORA") ||
    name.includes("N. SRA") ||
    name.includes("SAO ") ||
    name.includes("SÃO ") ||
    name.includes("SANTO") ||
    name.includes("SANTA") ||
    name.includes(" FÉ") ||
    name.startsWith("FÉ") ||
    name.includes(" FE ") ||
    name.includes("CRUCIFIXO") ||
    category.includes("SACRO") ||
    category.includes("RELIGIOSO")
  ) {
    return "SACRO";
  }

  // 3. ESPIRITUALISTA:
  if (
    sku.includes("ESP") ||
    name.includes("UMBANDA") ||
    name.includes("CANDOMBLÉ") ||
    name.includes("CANDOMBLE") ||
    name.includes("ORIXÁ") ||
    name.includes("ORIXA") ||
    name.includes("IANSÃ") ||
    name.includes("IANSA") ||
    name.includes("OGUM") ||
    name.includes("OXUM") ||
    name.includes("IEMANJÁ") ||
    name.includes("IEMANJA") ||
    name.includes("EXU") ||
    name.includes("OXÓSSI") ||
    name.includes("OXOSSI") ||
    name.includes("XANGÔ") ||
    name.includes("XANGO") ||
    category.includes("ESPIRITUALISTA") ||
    category.includes("MÍSTICO") ||
    category.includes("MISTICO")
  ) {
    return "ESPIRITUALISTA";
  }

  // 4. FORMATURA:
  if (
    sku.includes("FOR") ||
    sku.includes("FORM") ||
    name.includes("FORMATURA") ||
    name.includes("FORMANDO") ||
    name.includes("PINGENTE FORMATURA") ||
    category.includes("FORMATURA")
  ) {
    return "FORMATURA";
  }

  // 5. BERLOQUES:
  if (
    sku.includes("BER") ||
    sku.includes("BERL") ||
    name.includes("BERLOQUE") ||
    name.includes("PANDORA") ||
    name.includes("CHARM") ||
    category.includes("BERLOQUE") ||
    category.includes("PINGENTE BERLOQUE")
  ) {
    return "BERLOQUES";
  }

  return "OUTROS";
};

export const planningService = {
  // --- 0. CONFIGURAÇÕES ---
  async fetchConfig() {
    try {
      const records = await pb.collection("planejamento_configuracoes").getFullList();
      if (records.length > 0) {
        return records[0];
      }
      const defaultRecord = {
        prazo_fabricacao_dias: 15,
        buffer_seguranca_percentual: 30,
        janela_historico_meses: 6,
        oficina_capacidade_semanal: 0,
      };
      try {
        return await pb.collection("planejamento_configuracoes").create(defaultRecord);
      } catch (e) {
        return { id: "default", ...defaultRecord };
      }
    } catch (err) {
      console.warn("Fallback to default/local planning configs:", err);
      try {
        const local = localStorage.getItem("planejamento_configuracoes");
        if (local) {
          return JSON.parse(local);
        }
      } catch (e) {}
      return {
        id: "default",
        prazo_fabricacao_dias: 15,
        buffer_seguranca_percentual: 30,
        janela_historico_meses: 6,
        oficina_capacidade_semanal: 0,
      };
    }
  },

  async updateConfig(id, data, userId) {
    try {
      if (id === "default" || !id) {
        // Tenta salvar localmente antes
        try {
          localStorage.setItem("planejamento_configuracoes", JSON.stringify({ id: "default", ...data }));
        } catch (e) {}

        return await pb.collection("planejamento_configuracoes").create({
          ...data,
          updated_by: userId || "",
        });
      }
      const res = await pb.collection("planejamento_configuracoes").update(id, {
        ...data,
        updated_by: userId || "",
      });
      try {
        localStorage.setItem("planejamento_configuracoes", JSON.stringify(res));
      } catch (e) {}
      return res;
    } catch (err) {
      console.error("Error updating planning configurations:", err);
      // Se a coleção não existir no banco (404), salva localmente e finge sucesso para não travar a UI
      if (err.status === 404 || String(err.message).includes("collection context")) {
        try {
          localStorage.setItem("planejamento_configuracoes", JSON.stringify({ id: "default", ...data }));
          return { id: "default", ...data };
        } catch (e) {}
      }
      throw err;
    }
  },

  // --- 1. SAZONALIDADE ---
  async fetchSeasonality(catalogCategories, userId) {
    try {
      const records = await pb.collection("planejamento_sazonalidade").getFullList({
        sort: "categoria,mes",
      });

      // Se catalogCategories não vier, usamos PLANNING_CATEGORIES por padrão
      const targetCategories = (catalogCategories && catalogCategories.length > 0) 
        ? catalogCategories 
        : PLANNING_CATEGORIES;

      const existingKeys = new Set(records.map(r => `${r.categoria}_${r.mes}`));
      const toCreate = [];

      for (const cat of targetCategories) {
        for (let month = 1; month <= 12; month++) {
          const key = `${cat}_${month}`;
          if (!existingKeys.has(key)) {
            toCreate.push({
              categoria: cat,
              mes: month,
              fator: 1.0,
              history: [],
              updated_by: userId || "",
            });
          }
        }
      }

      if (toCreate.length > 0) {
        console.log(`🌱 Seeding ${toCreate.length} missing seasonality combinations...`);
        // PocketBase create promises
        await Promise.all(toCreate.map(item => pb.collection("planejamento_sazonalidade").create(item)));
        // Re-fetch records
        return await pb.collection("planejamento_sazonalidade").getFullList({
          sort: "categoria,mes",
        });
      }

      return records;
    } catch (err) {
      console.error("Error fetching seasonality:", err);
      return [];
    }
  },

  async updateSeasonalityCell(id, factor, oldFactor, userName, userId) {
    try {
      const record = await pb.collection("planejamento_sazonalidade").getOne(id);
      const history = Array.isArray(record.history) ? record.history : [];
      
      const newHistoryEntry = {
        updated_at: new Date().toISOString(),
        updated_by_name: userName || "Sistema",
        updated_by: userId || "",
        old_value: oldFactor,
        new_value: factor,
      };

      return await pb.collection("planejamento_sazonalidade").update(id, {
        fator: Number(factor),
        history: [...history, newHistoryEntry],
        updated_by: userId || "",
      });
    } catch (err) {
      console.error(`Error updating seasonality cell ${id}:`, err);
      throw err;
    }
  },

  async resetSeasonalityRow(category, userName, userId) {
    try {
      const records = await pb.collection("planejamento_sazonalidade").getFullList({
        filter: `categoria = "${category}"`,
      });

      const promises = records.map(r => {
        const history = Array.isArray(r.history) ? r.history : [];
        const newHistoryEntry = {
          updated_at: new Date().toISOString(),
          updated_by_name: userName || "Sistema",
          updated_by: userId || "",
          old_value: r.fator,
          new_value: 1.0,
          action: "row_reset"
        };
        return pb.collection("planejamento_sazonalidade").update(r.id, {
          fator: 1.0,
          history: [...history, newHistoryEntry],
          updated_by: userId || "",
        });
      });

      await Promise.all(promises);
    } catch (err) {
      console.error(`Error resetting seasonality for row ${category}:`, err);
      throw err;
    }
  },

  async resetAllSeasonality(userName, userId) {
    try {
      const records = await pb.collection("planejamento_sazonalidade").getFullList();
      const promises = records.map(r => {
        const history = Array.isArray(r.history) ? r.history : [];
        const newHistoryEntry = {
          updated_at: new Date().toISOString(),
          updated_by_name: userName || "Sistema",
          updated_by: userId || "",
          old_value: r.fator,
          new_value: 1.0,
          action: "global_reset"
        };
        return pb.collection("planejamento_sazonalidade").update(r.id, {
          fator: 1.0,
          history: [...history, newHistoryEntry],
          updated_by: userId || "",
        });
      });

      await Promise.all(promises);
    } catch (err) {
      console.error("Error resetting all seasonality:", err);
      throw err;
    }
  },

  // --- 2. ESTOQUES MÍNIMOS ---
  async fetchDefinedStocks() {
    try {
      return await pb.collection("planejamento_estoques_minimos").getFullList();
    } catch (err) {
      console.error("Error fetching defined stocks:", err);
      return [];
    }
  },

  async saveMinimumStock(sku, definedMin, suggestedMin, productName, category, userId) {
    try {
      // Find if record exists
      let existing = null;
      try {
        existing = await pb.collection("planejamento_estoques_minimos").getFirstListItem(`sku = "${sku}"`);
      } catch (e) {
        // Not found
      }

      const payload = {
        sku,
        produto_nome: productName,
        categoria: category,
        estoque_minimo_sugerido: suggestedMin,
        estoque_minimo_definido: definedMin,
        tem_minimo_definido: definedMin !== null,
        revisado_por: userId || "",
        ultima_revisao: new Date().toISOString(),
      };

      if (existing) {
        return await pb.collection("planejamento_estoques_minimos").update(existing.id, payload);
      } else {
        return await pb.collection("planejamento_estoques_minimos").create({
          ...payload,
          ativo: true,
        });
      }
    } catch (err) {
      console.error(`Error saving minimum stock for ${sku}:`, err);
      throw err;
    }
  },

  async toggleStockActive(sku, active, productName, category, userId) {
    try {
      let existing = null;
      try {
        existing = await pb.collection("planejamento_estoques_minimos").getFirstListItem(`sku = "${sku}"`);
      } catch (e) {
        // Not found
      }

      if (existing) {
        return await pb.collection("planejamento_estoques_minimos").update(existing.id, {
          ativo: active,
          revisado_por: userId || "",
          ultima_revisao: new Date().toISOString(),
        });
      } else {
        return await pb.collection("planejamento_estoques_minimos").create({
          sku,
          produto_nome: productName || "Sem Nome",
          categoria: category || "Sem Categoria",
          estoque_minimo_sugerido: 0,
          estoque_minimo_definido: 0,
          ativo: active,
          revisado_por: userId || "",
          ultima_revisao: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error(`Error toggling active state for SKU ${sku}:`, err);
      throw err;
    }
  },

  async acceptAllSuggestions(skusData, userId) {
    try {
      // skusData: Array of objects { sku, suggestedMin, productName, category }
      const promises = skusData.map(item => 
        this.saveMinimumStock(item.sku, item.suggestedMin, item.suggestedMin, item.productName, item.category, userId)
      );
      await Promise.all(promises);
    } catch (err) {
      console.error("Error accepting all suggestions:", err);
      throw err;
    }
  },

  // --- 3. LÓGICA DE CÁLCULO GERAL ---
  async loadPlanningData(params) {
    // params: { mesAlvo, prazoFabricacao, considerarSazonalidade, incluirAneisSemHistorico, catalog, inventory }
    const { mesAlvo, prazoFabricacao, considerarSazonalidade, incluirAneisSemHistorico, catalog, inventory } = params;

    // Fetch configs first from PocketBase
    let bufferSegurancaPercentual = 30;
    let janelaHistorico = 6;
    let prazoFabricacaoDias = prazoFabricacao || 15;
    
    try {
      const configRecords = await pb.collection("planejamento_configuracoes").getFullList();
      if (configRecords.length > 0) {
        const conf = configRecords[0];
        prazoFabricacaoDias = conf.prazo_fabricacao_dias !== undefined && conf.prazo_fabricacao_dias !== null ? Number(conf.prazo_fabricacao_dias) : prazoFabricacaoDias;
        bufferSegurancaPercentual = conf.buffer_seguranca_percentual !== undefined && conf.buffer_seguranca_percentual !== null ? Number(conf.buffer_seguranca_percentual) : bufferSegurancaPercentual;
        janelaHistorico = conf.janela_historico_meses !== undefined && conf.janela_historico_meses !== null ? Number(conf.janela_historico_meses) : janelaHistorico;
      }
    } catch (confErr) {
      console.warn("Could not load database configurations, using overrides or defaults:", confErr);
    }

    // 1. Fetch sales history for past (janelaHistorico * 30) days
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - (janelaHistorico * 30));
    const cutoffIso = cutoffDate.toISOString();

    const [soldRecords, archivedRecords, definedStocks, seasonalityList, lotesRascunho, produtoDadosList] = await Promise.all([
      pb.collection("inventory_items").getFullList({
        filter: `status = "sold" && dateOut >= "${cutoffIso}"`,
        fields: "sku,dateOut"
      }).catch(err => {
        console.error("Error fetching inventory_items in loadPlanningData:", err);
        return [];
      }),
      pb.collection("production_orders").getFullList({
        filter: `status = "PEDIDO_PRONTO" && updated >= "${cutoffIso}" && logistics_status != "CANCELADO" && logistics_status != "GOLPE"`,
        fields: "sku,updated,logistics_status"
      }).catch(err => {
        console.error("Error fetching production_orders in loadPlanningData:", err);
        return [];
      }),
      this.fetchDefinedStocks(),
      pb.collection("planejamento_sazonalidade").getFullList().catch(err => {
        console.error("Error fetching seasonality list in loadPlanningData:", err);
        return [];
      }),
      pb.collection("estoque_lotes").getFullList({
        filter: 'status = "rascunho" || status = "aguardando_aprovacao" || status = "solicitado"',
        fields: "itens,itens_planejados,items"
      }).catch(() => []),
      pb.collection("produto_dados").getFullList().catch(err => {
        console.warn("Aviso ao carregar produto_dados no planejamento:", err);
        return [];
      })
    ]);

    const produtoDadosMapBySkuPai = {};
    (produtoDadosList || []).forEach(d => {
      if (d.sku_pai) {
        produtoDadosMapBySkuPai[resolveCatalogSku(d.sku_pai, catalog)?.sku || d.sku_pai.trim().toUpperCase()] = d;
      }
    });

    const nonConfirmedPipelineMap = {};
    lotesRascunho.forEach(l => {
      const items = Array.isArray(l.itens) ? l.itens : [];
      items.forEach(itm => {
        if (itm.sku) {
          const skuKey = itm.sku.trim().toUpperCase();
          const q = Number(itm.quantidade || itm.qtd || 0);
          nonConfirmedPipelineMap[skuKey] = (nonConfirmedPipelineMap[skuKey] || 0) + q;
        }
      });
    });

    // Create defined stocks map
    const definedStocksMap = {};
    definedStocks.forEach(d => {
      definedStocksMap[d.sku] = d;
    });

    // Create seasonality maps
    const seasonalityMap = {};
    seasonalityList.forEach(s => {
      if (!seasonalityMap[s.categoria]) seasonalityMap[s.categoria] = {};
      seasonalityMap[s.categoria][s.mes] = s.fator;
    });

    // Count sales per complete SKU
    const salesCounts = {};
    const incrementSale = (sku) => {
      if (!sku) return;
      const key = sku.trim().toUpperCase();
      salesCounts[key] = (salesCounts[key] || 0) + 1;
    };

    soldRecords.forEach(r => incrementSale(r.sku));
    archivedRecords.forEach(r => incrementSale(r.sku));

    // Get all unique active complete SKUs
    const completeSkus = new Set();

    // A. Add SKUs that have sales history
    Object.keys(salesCounts).forEach(sku => completeSkus.add(sku));

    // B. Add SKUs currently in stock
    inventory.forEach(item => {
      if (item.sku && item.status === "in_stock") {
        completeSkus.add(item.sku.trim().toUpperCase());
      }
    });

    // C. Add SKUs from defined stocks
    definedStocks.forEach(d => completeSkus.add(d.sku));

    // D. Add all non-ring SKUs from catalog
    // E. If includeRingSizesWithoutHistory is true, generate sizes 10 to 30 for all rings in catalog
    Object.values(catalog).forEach(item => {
      const baseSku = item.sku;
      if (isRing(baseSku, item.category, catalog)) {
        if (incluirAneisSemHistorico) {
          ALL_RING_SIZES.forEach(size => {
            completeSkus.add(getRingSkuWithSize(baseSku, size));
          });
        }
      } else {
        completeSkus.add(baseSku);
      }
    });

    // Compute metrics for each complete SKU
    const resultItems = [];
    const now = new Date();
    const mesHoje = now.getMonth() + 1; // 1-12
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + prazoFabricacaoDias);
    const mesEntrega = deliveryDate.getMonth() + 1; // 1-12

    // Build the list of last 6 months (YYYY-MM)
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      last6Months.push(`${year}-${month}`);
    }

    completeSkus.forEach(sku => {
      // Skip if SKU is empty or descontinuado (defined as full_data.status = 'inactive' in catalog)
      // Check catalog by baseSku or completeSku
      let catalogItem = findItemInCatalog(sku, catalog);
      let ringSize = "";
      let baseSku = sku;

      if (isRing(sku, undefined, catalog)) {
        const parsed = parseRingSku(sku);
        baseSku = parsed.baseSku;
        ringSize = parsed.size;
        catalogItem = findItemInCatalog(baseSku, catalog) || catalogItem;
      }

      const isCatalogInactive = catalogItem ? catalogItem.status === "inactive" || catalogItem.full_data?.status === "inactive" : false;
      if (isCatalogInactive) {
        return; // Excluir SKUs descontinuados/inativos
      }

      const definedRecord = definedStocksMap[sku];
      const isRecordActive = definedRecord ? definedRecord.ativo !== false : true;

      const productName = catalogItem ? catalogItem.name + (ringSize ? ` (Aro ${ringSize})` : "") : sku;
      const category = catalogItem ? resolvePlanningCategory(catalogItem) : "OUTROS";

      // 1. Média de vendas por SKU
      const count = salesCounts[sku] || 0;
      const mediaMensal = Math.max(0, Math.round((count / janelaHistorico) * 10) / 10);

      // Monthly sales history breakdown
      const monthlySales = {};
      last6Months.forEach(m => { monthlySales[m] = 0; });

      const skuSales = [
        ...soldRecords.filter(r => r.sku && r.sku.trim().toUpperCase() === sku),
        ...archivedRecords.filter(r => r.sku && r.sku.trim().toUpperCase() === sku)
      ];

      skuSales.forEach(sale => {
        const dateStr = sale.dateOut || sale.updated;
        if (dateStr) {
          const yyyymm = dateStr.substring(0, 7); // "YYYY-MM"
          if (monthlySales[yyyymm] !== undefined) {
            monthlySales[yyyymm]++;
          }
        }
      });

      const historico_mensal = last6Months.map(m => ({
        mes: m,
        vendas: monthlySales[m]
      }));

      // 2. Fator de Sazonalidade
      let fator = 1.0;
      if (considerarSazonalidade && catalogItem) {
        fator = (seasonalityMap[category] && seasonalityMap[category][mesAlvo]) !== undefined
          ? seasonalityMap[category][mesAlvo]
          : 1.0;
      }

      // Previsão para o próximo período
      const fatorMesAlvo = (seasonalityMap[category] && seasonalityMap[category][mesAlvo]) !== undefined
        ? seasonalityMap[category][mesAlvo]
        : 1.0;
      const fatorMesEntrega = (seasonalityMap[category] && seasonalityMap[category][mesEntrega]) !== undefined
        ? seasonalityMap[category][mesEntrega]
        : 1.0;
      const fatorAplicado = Math.max(fatorMesAlvo, fatorMesEntrega);
      const previsaoProximos30Dias = Math.ceil(mediaMensal * fatorAplicado);

      // Resolução Canônica do Produto (PR1 & P4.3)
      const skuPaiKey = catalogItem?.sku || baseSku;
      const canonicalDados = produtoDadosMapBySkuPai[skuPaiKey] || null;

      // 1. Tempo de produção em dias úteis do produto (com fallback para config global)
      const tempoProducaoProduto = (canonicalDados?.ficha_tecnica?.tempo_producao_dias !== undefined && Number(canonicalDados?.ficha_tecnica?.tempo_producao_dias) > 0)
        ? Number(canonicalDados.ficha_tecnica.tempo_producao_dias)
        : prazoFabricacaoDias;

      // 2. Custo BOM real do produto em centavos e BRL
      const custoBomCentavos = canonicalDados?.custo_bom_centavos || 0;
      const custoBomBRL = custoBomCentavos > 0 ? custoBomCentavos / 100 : 0;

      // 3. Estoque mínimo sugerido baseado no tempo de produção real do produto
      const prazoEmMeses = tempoProducaoProduto / 30;
      const demandaDuranteFabricacao = Math.ceil(mediaMensal * prazoEmMeses * fatorAplicado);
      const buffer = Math.ceil(mediaMensal * (bufferSegurancaPercentual / 100));
      let estoqueMinimoSugerido = demandaDuranteFabricacao + buffer;
      if (mediaMensal === 0) {
        estoqueMinimoSugerido = 0;
      }
      estoqueMinimoSugerido = Math.max(0, estoqueMinimoSugerido);

      // 4. Estoque mínimo definido (respeita ficha_tecnica.estoque_minimo se houver)
      const estoqueMinimoFicha = (canonicalDados?.ficha_tecnica?.estoque_minimo !== undefined && Number(canonicalDados?.ficha_tecnica?.estoque_minimo) > 0)
        ? Number(canonicalDados.ficha_tecnica.estoque_minimo)
        : null;

      const estoqueMinimoDefinido = (definedRecord && definedRecord.estoque_minimo_definido !== undefined && definedRecord.estoque_minimo_definido !== null)
        ? definedRecord.estoque_minimo_definido
        : estoqueMinimoFicha;

      const effectiveMin = estoqueMinimoDefinido !== null ? estoqueMinimoDefinido : estoqueMinimoSugerido;

      // 5. Estoque atual (pronto)
      const estoqueAtual = inventory.filter(i => i.sku === sku && i.status === "in_stock").length;

      // 5b. Peças em produção (ativas e não suspensas/interceptadas)
      const emProducao = inventory.filter(i => i.sku === sku && i.status !== "in_stock" && i.status !== "sold" && i.status !== "adjusted_out" && i.status !== "pe_interceptado" && i.situacao_fluxo !== "suspenso").length;

      // 5c. Pipeline não confirmado (rascunho/solicitado)
      const pipelineNaoConfirmado = nonConfirmedPipelineMap[sku] || 0;

      // 6. Lacuna (desconta o estoque pronto e o que já está na oficina em andamento)
      const lacuna = Math.max(0, effectiveMin - (estoqueAtual + emProducao));

      // 7. Quantidade sugerida para fabricação
      let quantidadeSugerida = 0;
      if (lacuna > 0) {
        const loteEficiente = Math.max(lacuna, Math.ceil(mediaMensal));
        quantidadeSugerida = loteEficiente;
      }

      // 8. Calcular Prioridade
      let prioridade = "ok";
      if (mediaMensal === 0) {
        prioridade = "sem_historico";
      } else if (estoqueAtual === 0 && previsaoProximos30Dias > 0) {
        prioridade = "urgente";
      } else if (estoqueAtual > 0 && estoqueAtual < effectiveMin * 0.3) {
        prioridade = "critico";
      } else if (estoqueAtual >= effectiveMin * 0.3 && estoqueAtual < effectiveMin * 0.7) {
        prioridade = "alto";
      } else if (estoqueAtual >= effectiveMin * 0.7 && estoqueAtual < effectiveMin) {
        prioridade = "medio";
      } else if (estoqueAtual >= effectiveMin) {
        prioridade = "ok";
      }

      const investimentoSugeridoBRL = Math.round((quantidadeSugerida * custoBomBRL) * 100) / 100;

      resultItems.push({
        sku,
        baseSku,
        ringSize,
        produto_nome: productName,
        categoria: category,
        vendas_6_meses: count,
        media_mensal: mediaMensal,
        historico_mensal,
        previsao_proximos_30_dias: previsaoProximos30Dias,
        tempo_producao_dias: tempoProducaoProduto,
        estoque_minimo_sugerido: estoqueMinimoSugerido,
        estoque_minimo_definido: estoqueMinimoDefinido,
        tem_minimo_definido: estoqueMinimoDefinido !== null,
        estoque_atual: estoqueAtual,
        em_producao: emProducao,
        pipeline_nao_confirmado: pipelineNaoConfirmado,
        lacuna,
        quantidade_sugerida: quantidadeSugerida,
        quantidade_aprovada: quantidadeSugerida, // Padrão igual sugerida
        custo_bom_centavos: custoBomCentavos,
        custo_bom_brl: custoBomBRL,
        investimento_sugerido_brl: investimentoSugeridoBRL,
        canonical_dados: canonicalDados,
        prioridade,
        fator_sazonalidade_aplicado: fator,
        incluido: lacuna > 0 && isRecordActive,
        ativo: isRecordActive,
        isCatalogInactive,
      });
    });

    return {
      items: resultItems,
      seasonalityMap,
      definedStocksMap,
      configs: {
        prazoFabricacaoDias,
        bufferSegurancaPercentual,
        janelaHistorico
      }
    };
  },

  // --- 4. GERAÇÃO DE DEMANDA ---
  async saveDemanda(demandaData, itemsData, user) {
    try {
      // 1. Create header record in planejamento_demandas
      const demandaHeader = await pb.collection("planejamento_demandas").create({
        status: "rascunho",
        periodo_referencia_inicio: demandaData.refInicio,
        periodo_referencia_fim: demandaData.refFim,
        mes_alvo: Number(demandaData.mesAlvo),
        prazo_fabricacao_dias: Number(demandaData.prazoFabricacao),
        data_entrega_prevista: demandaData.dataEntregaPrevista,
        total_skus_analisados: Number(demandaData.totalAnalisados),
        total_skus_com_lacuna: Number(demandaData.totalComLacuna),
        total_pecas_solicitadas: Number(demandaData.totalPecas),
        total_lotes: 0,
        gerado_por: user?.id || "",
        observacoes: demandaData.observacoes || "",
      });

      // 2. Create item records in planejamento_demanda_items
      const promises = itemsData.map(item => 
        pb.collection("planejamento_demanda_items").create({
          demanda_id: demandaHeader.id,
          sku: item.sku,
          produto_nome: item.produto_nome,
          categoria: item.categoria,
          estoque_atual: Number(item.estoque_atual),
          estoque_minimo: Number(item.estoque_minimo_definido),
          lacuna: Number(item.lacuna),
          quantidade_sugerida: Number(item.quantidade_sugerida),
          quantidade_aprovada: Number(item.quantidade_aprovada),
          prioridade: item.prioridade,
          fator_sazonalidade_aplicado: Number(item.fator_sazonalidade_aplicado),
          pressao_sazonalidade: Number(item.pressao_sazonalidade),
          status_item: "pendente",
          enviado_fabrica: false,
          fabrica_item_id: [],
        })
      );
      await Promise.all(promises);

      return demandaHeader;
    } catch (err) {
      console.error("Error saving planning demand:", err);
      throw err;
    }
  },

  async updateDemandaStatus(demandaId, status, approverId) {
    try {
      const payload = { status };
      if (approverId && (status === "aprovado" || status === "enviado_fabrica" || status === "enviado_parcial")) {
        payload.aprovado_por = approverId;
      }
      return await pb.collection("planejamento_demandas").update(demandaId, payload);
    } catch (e) {
      console.error("Error updating demand status:", e);
      throw e;
    }
  },

  // --- 5. GESTÃO DE LOTES ---
  async createLote(loteData) {
    try {
      // Pega a demanda correspondente para obter o prazo
      let prazo = 15;
      if (loteData.demanda_id) {
        try {
          const demand = await pb.collection("planejamento_demandas").getOne(loteData.demanda_id);
          prazo = demand.prazo_fabricacao_dias || 15;
        } catch (e) {
          console.warn("Could not get demand for prazo, using fallback", e);
        }
      } else {
        try {
          const configs = await this.fetchConfig();
          prazo = configs.prazo_fabricacao_dias || 15;
        } catch (e) {
          console.warn("Could not get configs for prazo, using fallback", e);
        }
      }

      const dataInicio = new Date(loteData.data_inicio_prevista);
      const dataEntrega = new Date(dataInicio);
      dataEntrega.setDate(dataEntrega.getDate() + prazo);

      return await pb.collection("planejamento_lotes").create({
        demanda_id: loteData.demanda_id || "",
        nome: loteData.nome,
        status: "rascunho",
        prioridade_geral: loteData.prioridade_geral || "medio",
        data_inicio_prevista: dataInicio.toISOString(),
        data_entrega_prevista: dataEntrega.toISOString(),
        total_itens: 0,
        total_pecas: 0,
        observacoes: loteData.observacoes || "",
      });
    } catch (err) {
      console.error("Error creating planning batch (lote):", err);
      throw err;
    }
  },

  async updateLoteItems(loteId, itemIds, demandaId) {
    try {
      // 1. Remove items previously in this lote (reset to NULL)
      const prevItems = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`,
      });
      await Promise.all(prevItems.map(item => 
        pb.collection("planejamento_demanda_items").update(item.id, {
          lote_id: "",
          status_item: "pendente",
        })
      ));

      // 2. Set new lote_id for selected itemIds
      if (itemIds && itemIds.length > 0) {
        await Promise.all(itemIds.map(id => 
          pb.collection("planejamento_demanda_items").update(id, {
            lote_id: loteId,
            status_item: "alocado_lote",
          })
        ));
      }

      // 3. Recalculate totals of this lote
      const updatedItems = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`,
      });

      const totalItens = updatedItems.length;
      const totalPecas = updatedItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      // Determine general priority (most urgent)
      const priorityOrder = { urgente: 6, critico: 5, alto_sazonal: 4, alto: 3, medio_sazonal: 2, medio: 1, ok: 0 };
      let highestPriority = "medio";
      let maxScore = -1;
      updatedItems.forEach(item => {
        const score = priorityOrder[item.prioridade] || 0;
        if (score > maxScore) {
          maxScore = score;
          highestPriority = item.prioridade;
        }
      });

      await pb.collection("planejamento_lotes").update(loteId, {
        total_itens: totalItens,
        total_pecas: totalPecas,
        prioridade_geral: highestPriority,
      });

      // 4. Update the demand's total pieces / lotes counters if demandaId is present
      if (demandaId) {
        const allLotes = await pb.collection("planejamento_lotes").getFullList({
          filter: `demanda_id = "${demandaId}"`,
        });
        await pb.collection("planejamento_demandas").update(demandaId, {
          total_lotes: allLotes.length,
        });
      }
    } catch (err) {
      console.error("Error updating batch (lote) items:", err);
      throw err;
    }
  },

  async addManualItemToLote(loteId, sku, qtd, priority = "Planejado") {
    const cleanSku = String(sku).trim().toUpperCase();
    const quantity = Number(qtd) || 1;

    try {
      const existing = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}" && sku = "${cleanSku}"`
      });

      if (existing && existing.length > 0) {
        await pb.collection("planejamento_demanda_items").update(existing[0].id, {
          quantidade_aprovada: (existing[0].quantidade_aprovada || 0) + quantity,
          prioridade: priority
        });
      } else {
        await pb.collection("planejamento_demanda_items").create({
          lote_id: loteId,
          sku: cleanSku,
          quantidade_aprovada: quantity,
          prioridade: priority,
          status_item: "alocado_lote"
        });
      }

      const updatedItems = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`,
      });
      const totalItens = updatedItems.length;
      const totalPecas = updatedItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      await pb.collection("planejamento_lotes").update(loteId, {
        total_itens: totalItens,
        total_pecas: totalPecas
      });

      return updatedItems;
    } catch (err) {
      console.error("Error adding manual item to lote:", err);
      throw err;
    }
  },

  // --- 6. ENVIO DE LOTE PARA A FÁBRICA ---
  async sendLoteToFactory(loteId, user) {
    try {
      const lote = await pb.collection("planejamento_lotes").getOne(loteId);
      if (lote.status === "enviado_fabrica") {
        throw new Error("Este lote já foi enviado para a fábrica.");
      }

      // 1. Get all items in this lote
      const items = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`,
      });

      const itensPlanejadosLote = [];
      const sentItemIds = [];

      for (const item of items) {
        const qty = item.quantidade_aprovada || 0;
        if (qty <= 0) {
          // Reject item logically
          await pb.collection("planejamento_demanda_items").update(item.id, {
            status_item: "rejeitado",
          });
          continue;
        }

        itensPlanejadosLote.push({
          sku: item.sku.toUpperCase().trim(),
          qtd: qty
        });

        // 5. Update planning item with references
        await pb.collection("planejamento_demanda_items").update(item.id, {
          status_item: "enviado",
          enviado_fabrica: true,
          fabrica_item_id: [], // Deixado vazio até o gestor aprovar e gerar itens reais
        });

        sentItemIds.push(item.id);
      }

      // Cria lote em estoque_lotes com status 'aguardando_aprovacao' no buffer do gestor
      if (itensPlanejadosLote.length > 0) {
        await loteService.createDraftLote(
          itensPlanejadosLote,
          user?.id || null,
          user?.name || "Planejamento",
          `Origem: Planejamento (Lote de Demanda: ${lote.nome})`,
          "planejamento",
          "aguardando_aprovacao"
        );
      }

      // 6. Mark batch (lote) as sent
      await pb.collection("planejamento_lotes").update(loteId, {
        status: "enviado_fabrica",
        enviado_em: new Date().toISOString(),
        enviado_by: user?.id || "",
        enviado_por: user?.id || "",
      });

      // 7. Check overall demand state if demand_id exists
      const demandId = lote.demanda_id;
      if (demandId) {
        const allLotes = await pb.collection("planejamento_lotes").getFullList({
          filter: `demanda_id = "${demandId}"`,
        });

        const unsentLotes = allLotes.filter(l => l.status !== "enviado_fabrica" && l.status !== "cancelado");
        
        let newDemandStatus = "enviado_parcial";
        if (unsentLotes.length === 0) {
          newDemandStatus = "enviado_fabrica";
        }

        await pb.collection("planejamento_demandas").update(demandId, {
          status: newDemandStatus,
        });
      }

      return { success: true };
    } catch (err) {
      console.error("Error sending batch (lote) to factory:", err);
      throw err;
    }
  },

  async deleteDemanda(demandaId) {
    try {
      // 1. Fetch the demand record to verify status
      const demand = await pb.collection("planejamento_demandas").getOne(demandaId);
      if (demand.status !== "rascunho") {
        throw new Error("Não é possível excluir um planejamento que já foi aprovado ou enviado para a fábrica.");
      }

      // 2. Fetch all items of this demand
      const items = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `demanda_id = "${demandaId}"`,
      });

      // 3. Delete all items associated with this demand
      await Promise.all(items.map(item => pb.collection("planejamento_demanda_items").delete(item.id)));

      // 4. Fetch and delete all batches (lotes) of this demand
      const lotes = await pb.collection("planejamento_lotes").getFullList({
        filter: `demanda_id = "${demandaId}"`,
      });
      await Promise.all(lotes.map(lote => pb.collection("planejamento_lotes").delete(lote.id)));

      // 5. Delete the demand header record
      await pb.collection("planejamento_demandas").delete(demandaId);

      return { success: true };
    } catch (err) {
      console.error("Error in deleteDemanda:", err);
      throw err;
    }
  },
};
